import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

function OrgLanding() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [org, setOrg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOrg();
  }, [slug]);

  const fetchOrg = async () => {
    try {
      const res = await axios.get(`/api/orgs/public/${slug}`);
      setOrg(res.data);
      document.title = `${res.data.name} on Belong — Anonymous Employee Communities`;
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Organization not found');
      } else {
        setError('Failed to load organization');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingCard}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorCard}>
          <h2>😕 {error}</h2>
          <p><Link to="/" style={{ color: '#7C3AED' }}>Go to Belong homepage</Link></p>
        </div>
      </div>
    );
  }

  const brandColor = org.brand_color || '#7C3AED';

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Header with org branding */}
        <div style={{ ...styles.header, background: `linear-gradient(135deg, ${brandColor}, ${brandColor}dd)` }}>
          {org.logo_url && <img src={org.logo_url} alt={org.name} style={styles.logo} />}
          <h1 style={styles.orgName}>{org.name}</h1>
          <p style={styles.tagline}>Anonymous Employee Communities on Belong</p>
        </div>

        {/* Value proposition */}
        <div style={styles.body}>
          <h2 style={styles.sectionTitle}>A safe space for your team</h2>
          <p style={styles.description}>
            {org.name} uses Belong to give employees anonymous, privacy-first communities
            for invisible identities — mental health, neurodiversity, chronic illness, and more.
          </p>

          {/* How it works */}
          <div style={styles.features}>
            <div style={styles.feature}>
              <span style={styles.featureIcon}>👤</span>
              <div>
                <strong>100% Anonymous</strong>
                <p style={styles.featureDesc}>Your posts are anonymous. Your employer cannot see what you write.</p>
              </div>
            </div>
            <div style={styles.feature}>
              <span style={styles.featureIcon}>🛡️</span>
              <div>
                <strong>Employer-Invisible</strong>
                <p style={styles.featureDesc}>Your employer only sees aggregated metrics — never individual activity.</p>
              </div>
            </div>
            <div style={styles.feature}>
              <span style={styles.featureIcon}>🤝</span>
              <div>
                <strong>Connect on Your Terms</strong>
                <p style={styles.featureDesc}>Identity shared only through mutual consent, one level at a time.</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div style={styles.ctaSection}>
            <button
              onClick={() => navigate('/org/onboard')}
              style={{ ...styles.ctaBtn, backgroundColor: brandColor }}
            >
              Join {org.name}'s Community →
            </button>
            <p style={styles.ctaNote}>
              You'll need your <strong>{org.name}</strong> work email to join.
            </p>
          </div>

          {/* Trust footer */}
          <div style={styles.trustFooter}>
            <div style={styles.trustBadge}>
              🔒 Privacy guaranteed: your employer cannot see your posts, identities, or activity
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <p>Powered by <Link to="/" style={{ color: '#7C3AED', textDecoration: 'none', fontWeight: '600' }}>Belong</Link></p>
          <div style={styles.footerLinks}>
            <Link to="/privacy" style={styles.footerLink}>Privacy Policy</Link>
            <Link to="/terms" style={styles.footerLink}>Terms</Link>
            <Link to="/about" style={styles.footerLink}>About Belong</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: { minHeight: '100vh', backgroundColor: '#f3f4f6', display: 'flex', justifyContent: 'center', padding: '40px 16px' },
  card: { backgroundColor: '#fff', borderRadius: '16px', maxWidth: '600px', width: '100%', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' },
  loadingCard: { padding: '60px', textAlign: 'center', color: '#6b7280' },
  errorCard: { padding: '60px', textAlign: 'center', color: '#1f2937' },
  header: { padding: '48px 32px', textAlign: 'center', color: '#fff' },
  logo: { height: '64px', marginBottom: '16px', borderRadius: '12px' },
  orgName: { fontSize: '28px', fontWeight: '700', margin: '0 0 8px' },
  tagline: { fontSize: '15px', opacity: 0.9, margin: 0 },
  body: { padding: '32px' },
  sectionTitle: { fontSize: '20px', fontWeight: '700', color: '#1f2937', margin: '0 0 12px' },
  description: { fontSize: '15px', color: '#555', lineHeight: 1.6, marginBottom: '28px' },
  features: { display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '32px' },
  feature: { display: 'flex', gap: '14px', alignItems: 'flex-start' },
  featureIcon: { fontSize: '24px', flexShrink: 0, marginTop: '2px' },
  featureDesc: { fontSize: '13px', color: '#6b7280', margin: '4px 0 0', lineHeight: 1.4 },
  ctaSection: { textAlign: 'center', marginBottom: '24px' },
  ctaBtn: { display: 'inline-block', padding: '14px 32px', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: '600', cursor: 'pointer', width: '100%' },
  ctaNote: { fontSize: '13px', color: '#6b7280', marginTop: '12px' },
  trustFooter: { marginTop: '16px' },
  trustBadge: { backgroundColor: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#166534', textAlign: 'center' },
  footer: { padding: '20px 32px', borderTop: '1px solid #f3f4f6', textAlign: 'center', fontSize: '13px', color: '#999' },
  footerLinks: { display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '8px' },
  footerLink: { color: '#7C3AED', textDecoration: 'none', fontSize: '12px' },
};

export default OrgLanding;
